/**
 * Collection Card Images Enhancer
 *
 * Architecture:
 * - Content-map is the source of truth for which products appear on each occasion
 * - Liquid renders all products with skeleton placeholders (no image loading)
 * - This script reads content-map, hides unwanted products, loads images for visible ones
 *
 * Image Resolution Strategy (via ProductImages module):
 * 1. Check for explicit occasion images in the map
 * 2. Try occasion-specific product image: c-{occasion}-{abbrev}--{slot}.jpg
 * 3. If not found, fall back to generic product image: i-{abbrev}--{slot}.jpg
 *
 * Depends on:
 * - window.ProductImages from `product-images.js`
 */
(function () {
  'use strict';

  /**
   * Append Shopify CDN width and format parameters to image URL
   * @param {string} url - Original image URL
   * @param {number} width - Desired width in pixels
   * @returns {string} URL with optimization parameters
   */
  function getSizedImageUrl(url, width) {
    if (!url || !width) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}width=${width}&format=webp`;
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  /**
   * Get occasion handle from the current page
   * Checks collection handle first, then URL query param
   * @returns {string} Occasion handle or empty string
   */
  function getOccasionFromPage() {
    // Check for collection handle in data attribute
    const collectionEl = document.querySelector('[data-collection-handle]');
    if (collectionEl && collectionEl.dataset.collectionHandle) {
      return collectionEl.dataset.collectionHandle;
    }

    // Check URL path for collection
    const pathMatch = window.location.pathname.match(/\/collections\/([^\/\?]+)/);
    if (pathMatch) {
      return pathMatch[1];
    }

    // Check URL query param
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('occasion') || '';
  }

  /**
   * Get list of product handles that should be visible for this occasion
   * Reads from content-map's products_section.product_handles
   * Content-map now uses virtual handles directly (fridge-magnets, photo-magnets)
   * @param {Object} map - Content map object
   * @param {string} occasion - Collection/occasion handle
   * @returns {string[]} Array of display handles to show
   */
  function getVisibleProducts(map, occasion) {
    const allProducts = ['fridge-magnets', 'photo-magnets', 'calendar-magnets', 'puzzle-magnets', 'save-the-date'];

    // Check occasion-specific config first
    if (map.collections && map.collections[occasion]) {
      const collectionConfig = map.collections[occasion];
      if (collectionConfig.products_section && collectionConfig.products_section.product_handles) {
        return collectionConfig.products_section.product_handles;
      }
    }

    // Check defaults
    if (map.defaults && map.defaults.products_section && map.defaults.products_section.product_handles) {
      return map.defaults.products_section.product_handles;
    }

    // Ultimate fallback: show all products
    return allProducts;
  }

  /**
   * Initialize autoplay on a collection card slideshow.
   * Re-initializes the slider component after images load, then cycles
   * through slides every 4 s. Pauses on interaction, resumes after 6 s.
   * @param {Element} slideshowEl - The slideshow-component element
   */
  function initCardAutoplay(slideshowEl) {
    const slider = slideshowEl.querySelector('[id^="Slider-"]');
    if (!slider) return;

    const slides = Array.from(slider.querySelectorAll('.category-carousel__slide'));
    if (slides.length < 2) return;

    // Re-initialize the underlying SliderComponent so offsets are correct
    if (typeof slideshowEl.resetPages === 'function') {
      slideshowEl.resetPages();
    }

    let currentIdx = 0;
    let intervalId = null;
    let resumeTimeout = null;
    let paused = false;
    const speed = 4000;

    function syncDots(idx) {
      const dots = slideshowEl.querySelectorAll('.slider-counter__link');
      dots.forEach((d, i) => {
        d.classList.toggle('slider-counter__link--active', i === idx);
      });
    }

    function goTo(idx) {
      currentIdx = idx;
      slider.scrollTo({ left: slides[currentIdx].offsetLeft, behavior: 'smooth' });
      syncDots(currentIdx);
    }

    function advance() {
      goTo((currentIdx + 1) % slides.length);
    }

    function start() {
      if (intervalId) return;
      intervalId = setInterval(advance, speed);
    }

    function stop() {
      clearInterval(intervalId);
      intervalId = null;
    }

    function pause() {
      if (paused) return;
      paused = true;
      stop();
      clearTimeout(resumeTimeout);
      resumeTimeout = setTimeout(() => { paused = false; start(); }, 6000);
    }

    // Sync currentIdx when user manually swipes
    slider.addEventListener('scroll', () => {
      let closest = 0;
      let minDist = Infinity;
      slides.forEach((s, i) => {
        const dist = Math.abs(s.offsetLeft - slider.scrollLeft);
        if (dist < minDist) { minDist = dist; closest = i; }
      });
      if (closest !== currentIdx) {
        currentIdx = closest;
        syncDots(currentIdx);
      }
    });

    // Pause on interaction
    slideshowEl.addEventListener('pointerdown', pause);
    slideshowEl.addEventListener('touchstart', pause, { passive: true });
    slideshowEl.addEventListener('mouseenter', pause);

    // Wire prev / next buttons
    const prevBtn = slideshowEl.querySelector('button[name="previous"]');
    const nextBtn = slideshowEl.querySelector('button[name="next"]');
    if (prevBtn) prevBtn.addEventListener('click', (e) => {
      e.preventDefault(); pause();
      goTo((currentIdx - 1 + slides.length) % slides.length);
    });
    if (nextBtn) nextBtn.addEventListener('click', (e) => {
      e.preventDefault(); pause();
      goTo((currentIdx + 1) % slides.length);
    });

    // Wire dot buttons
    slideshowEl.querySelectorAll('.slider-counter__link').forEach((dot, i) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault(); pause();
        goTo(i);
      });
    });

    start();
  }

  async function enhance() {
    if (!window.ProductImages) return;

    const nodes = Array.from(document.querySelectorAll('[data-collection-card-images]'));
    if (!nodes.length) return;

    // Load map once (ProductImages already caches internally)
    const first = nodes[0];
    const mapUrl = first.dataset.mediaMapUrl;
    const assetsBase = first.dataset.assetsBase || '';
    const assetsVersion = first.dataset.assetsVersion || '';
    if (!mapUrl) return;

    let map;
    try {
      map = await window.ProductImages.loadMediaMap(mapUrl);
    } catch (e) {
      console.error('Failed to load content-map.json', e);
      return;
    }

    // Get occasion from URL or page
    const occasion = getOccasionFromPage();

    // Get which products should be visible for this occasion
    const visibleProducts = getVisibleProducts(map, occasion);

    for (const el of nodes) {
      const productHandle = el.dataset.productHandle || '';
      const elOccasion = el.dataset.occasionHandle || occasion || '';
      if (!productHandle) continue;

      // Find the parent product card element
      const card = el.closest('[data-product-card]');

      // Check if this product should be visible
      if (!visibleProducts.includes(productHandle)) {
        // Hide the entire product card
        if (card) {
          card.classList.add('category-carousel__item--hidden');
        }
        continue; // Skip image loading for hidden cards
      }

      // Ensure card is visible (in case it was hidden before)
      if (card) {
        card.classList.remove('category-carousel__item--hidden');
      }

      const imgs = Array.from(el.querySelectorAll('img[data-collection-card-image][data-slot]'));
      if (!imgs.length) continue;

      // Get resolved images using the shared async function
      const maxSlot = Math.max(...imgs.map(img => parseInt(img.dataset.slot, 10) || 0));
      const resolvedImages = await window.ProductImages.resolveImagesAsync(
        map,
        productHandle,
        elOccasion,
        maxSlot,
        assetsBase,
        assetsVersion
      );

      // Apply images to slots with optimized sizing
      for (const img of imgs) {
        const slot = parseInt(img.dataset.slot, 10);
        if (!slot || slot < 1) continue;

        const baseUrl = resolvedImages[slot - 1];
        if (!baseUrl) continue;

        // Apply width/WebP optimization (400px is appropriate for card images)
        const optimizedUrl = getSizedImageUrl(baseUrl, 400);

        // Set src (was empty) and remove skeleton class
        img.setAttribute('src', optimizedUrl);
        img.classList.remove('category-carousel__image--skeleton');

        // Debug: asset filename overlay on card image
        const filename = baseUrl.split('/').pop().split('?')[0];
        let debugSpan = img.parentElement?.querySelector('.debug-asset-filename');
        if (!debugSpan) {
          debugSpan = document.createElement('span');
          debugSpan.className = 'debug-asset-filename debug-asset-filename--card';
          if (img.parentElement) {
            img.parentElement.style.position = 'relative';
            img.parentElement.appendChild(debugSpan);
          }
        }
        debugSpan.textContent = filename;
      }

      // Re-initialize slider and start autoplay for this card
      initCardAutoplay(el);

      // Apply product title and subtitle from content-map
      if (card) {
        const productData = map.products?.[productHandle];
        if (productData) {
          const titleEl = card.querySelector('[data-card-title]');
          const subtitleEl = card.querySelector('[data-card-subtitle]');
          if (titleEl && productData.title) titleEl.textContent = productData.title;
          if (subtitleEl && productData.card_subtitle) subtitleEl.textContent = productData.card_subtitle;
        }

        // Apply product URLs from content-map (using shopify_url)
        const productLinks = card.querySelectorAll('[data-product-link]');
        for (const link of productLinks) {
          const linkHandle = link.dataset.productHandle || productHandle;
          const linkOccasion = link.dataset.occasion || elOccasion;

          // Get base URL from content-map
          let url = window.ProductImages.getProductUrl(map, linkHandle);

          // Append occasion parameter if present
          if (linkOccasion) {
            const separator = url.includes('?') ? '&' : '?';
            url = `${url}${separator}occasion=${linkOccasion}`;
          }

          link.setAttribute('href', url);
        }
      }
    }
  }

  function prefetchCardImages() {
    var seen = {};
    document.querySelectorAll('img[data-collection-card-image][src]:not([src=""])').forEach(function (img) {
      var src = img.getAttribute('src');
      if (!src || seen[src]) return;
      seen[src] = true;
      if (document.querySelector('link[rel="prefetch"][href="' + CSS.escape(src) + '"]')) return;
      var link = document.createElement('link');
      link.rel = 'prefetch';
      link.as = 'image';
      link.href = src;
      document.head.appendChild(link);
    });
  }

  ready(() => {
    // Defer one tick so markup is in place even with sections re-rendering.
    setTimeout(async function () {
      await enhance();
      if ('requestIdleCallback' in window) {
        requestIdleCallback(prefetchCardImages, { timeout: 3000 });
      } else {
        setTimeout(prefetchCardImages, 2000);
      }
    }, 0);
  });
})();
